import * as serverActionCreators from '@module/license/actionCreators/hardwareActionCreators';
import * as licenseAllowanceActionCreators from '@module/license/actionCreators/licenseAllowanceActionCreators';
import * as serverWithServicesActionCreators from '@module/license/actionCreators/machineActionCreators';
import * as siteLicenseActionCreators from '@module/license/actionCreators/siteLicenseActionCreators';
import { isValidHardwareIdInput, isValidServerNameInput, SERVER_DESCRIPTION_MAX_LENGTH, SERVER_ID_MAX_LENGTH } from '@service/constants';
import moment from 'moment';
import * as LicenseModels from './models';

const initialSiteLicenseState: LicenseModels.ISiteLicenseState = {
    licenseFileLoading: false
};

const initialCustomerSiteState: { customerSite: LicenseModels.ICustomerSite } = {
    customerSite: {
        name: '',
        physicalAddress: '',
        region: '',
        shippingAddress: '',
        site: '',
        siteID: ''
    }
};

const initialServerState: LicenseModels.IServerState = {
    server: [],
    numberOfSavedServers: 0,
    serverLoading: false,
    serverIsAddingNew: false,
    serverEditing: false,
    availableServices: [],
    operationalUsages: [],
    serverErrorMessageVisible: false
};

const initialLicenseAllocationState: LicenseModels.ILicenseAllowanceState = {
    site: {
        id: 0,
        servIsId: '0',
        siteName: '',
        owner: '',
        operator: '',
        regionName: '',
        country: '',
        city: ''
    },
    licenseAllowances: [],
    licenseAllowancesIsAddingNew: false,
    licenseAllowancesIsEditing: false,
    licenseAllowancesLoading: false,
    licenseAllowanceErrorMessageVisible: false
};

const initialServiceOptionsState: LicenseModels.IServiceOptionsState = {
    serviceOptions: {
        licensableServices: [],
        poNumber: '',
        totalAssetAllocation: 0,
        expirationDateUtc: moment()
    },
    serviceOptionsLoading: false
};

// TODO: should these be broken out into the component level?
export function serverReducer(state = initialServerState, action: any): any {
    switch (action.type) {
        case serverActionCreators.SERVER_GET_REQUEST:
            return {
                ...state,
                serverLoading: true
            };
        case serverActionCreators.SERVER_GET_SUCCESS: {

            const serversWithUpdatedVisibility = action.server.map((x: any) => {
                return { ...x,
                    isAvailable: true
                };
            });
            return { ...state,
                server: serversWithUpdatedVisibility,
                numberOfSavedServers: action.server.length,
                serverLoading: false,
                availableServices: action.services
            };
        }
        case serverActionCreators.OPERATIONAL_USAGE_GET_SUCCESS:
            return {
                ...state,
                operationalUsages: action.operationalUsages
            };
        case serverActionCreators.SERVER_GET_ERROR:
            // TODO: Add and display error
            return {
                ...state,
                text: action.text,
                serverLoading: false,
                serverErrorMessageVisible: true
            };
        case serverActionCreators.SERVER_ADD_REQUEST:
            return { ...state,
                serverIsAddingNew: true,
                server:
                    state.server
                        .concat([action.newServer])
            };
        case serverActionCreators.SERVER_DELETE_REQUEST:
            return {
                ...state,
                serverLoading: false,
                serverIsAddingNew: false,
            };
        case serverActionCreators.SERVER_DELETE_SUCCESS:
            return { ...state,
                serverIsAddingNew: false,
                server:
                    state.server
                        .filter((server: any) =>
                            server.id !== action.server.id),
                numberOfSavedServers: action.server.id === -1 ? state.numberOfSavedServers : state.numberOfSavedServers - 1
            };
        case serverActionCreators.SERVER_DELETE_ERROR:
            // TODO: Add and display error
            return {
                ...state,
                text: action.text,
                serverLoading: false,
                serverErrorMessageVisible: true
            };
        case serverActionCreators.SERVER_EDIT_ROW: {
            const hwToEditIndex = state.server.indexOf(action.serverToEdit);
            if (hwToEditIndex > -1) {
                state.server[hwToEditIndex].isBeingEdited = true;
            }
            return {
                ...state,
                server: state.server,
                serverEditing: true,
                serverIsAddingNew: false,
            };
        }
        case serverActionCreators.SERVER_UPDATE_REQUEST: {
            const hwToUpdateIndex = state.server.indexOf(action.serverToUpdate);
            if (hwToUpdateIndex > -1) {
                state.server[hwToUpdateIndex].isBeingEdited = false;
            }
            return {
                ...state,
                hardwareIsEditing: false,
                serverIsAddingNew: false
            };
        }
        case serverActionCreators.SERVER_DESCRIPTION_UPDATE: {
            const indexToUpdate = state.server.map((server) => server.isBeingEdited).indexOf(true);
            return { ...state,
                serverIsAddingNew: false,
                server:
                    state.server.slice(0, indexToUpdate)
                        .concat([{
                            ...state.server[indexToUpdate],
                            description: {
                                value: action.newDescription,
                                valid: isValidServerNameInput(action.newDescription, SERVER_DESCRIPTION_MAX_LENGTH) && state.server.filter((server: LicenseModels.IServer) => server.description.value === action.newDescription).length < 1
                            }
                        }])
                        .concat(state.server.slice(indexToUpdate + 1))
            };
        }
        case serverActionCreators.SERVER_SERVERID_UPDATE: {
            const indexToUpdateServerId = state.server.map((server) => server.isBeingEdited).indexOf(true);
            return { ...state,
                serverIsAddingNew: false,
                server:
                    state.server.slice(0, indexToUpdateServerId)
                        .concat([{
                            ...state.server[indexToUpdateServerId],
                            serverId: {
                                value: action.newServerId,
                                valid: isValidHardwareIdInput(action.newServerId, SERVER_ID_MAX_LENGTH)
                            }
                        }])
                        .concat(state.server.slice(indexToUpdateServerId + 1))
            };
        }
        case serverActionCreators.SERVER_OPERATIONAL_UPDATE: {
            const indexUpdate = state.server.map((server) => server.isBeingEdited).indexOf(true);
            return { ...state,
                serverIsAddingNew: false,
                server:
                    state.server.slice(0, indexUpdate)
                        .concat([{
                            ...state.server[indexUpdate],
                            operationalUseType: {
                                id: action.value.id,
                                usageName: action.value.label
                            }
                        }])
                        .concat(state.server.slice(indexUpdate + 1))
            };
        }
        case serverActionCreators.SERVER_RESET_CURRENTLY_EDITING: {
            const indexUpdateEditing = state.server.map((server) => server.isBeingEdited).indexOf(true);
            const currentServer = indexUpdateEditing > -1 ? { ...state,
                server:
                    state.server.slice(0, indexUpdateEditing)
                        .concat([{
                            ...state.server[indexUpdateEditing],
                            isBeingEdited: false
                        }])
                        .concat(state.server.slice(indexUpdateEditing + 1))
            } : state;
            return { ...currentServer,
                hardwareIsEditing: true,
                server: currentServer.server
                    .filter((server: any) =>
                        server.id !== -1)
            };
        }

        case serverActionCreators.SERVER_CANCEL_EDITING: {
            const indexEditing = state.server.map((server) => server.isBeingEdited).indexOf(true);
            const serverEditing = indexEditing > -1 ? { ...state,
                server:
                    state.server.slice(0, indexEditing)
                        .concat([{
                            ...action.serverPreEdit,
                            isBeingEdited: false
                        }])
                        .concat(state.server.slice(indexEditing + 1))
            } : state;
            return { ...serverEditing,
                hardwareIsEditing: false,
                server: serverEditing.server
                    .filter((server: any) =>
                        server.id !== -1)
            };
        }

        case serverActionCreators.SERVER_UPDATE_SUCCESS:
            return {
                ...state,
                serverEditing: false
            };
        case serverActionCreators.SERVER_UPDATE_ERROR:
            // TODO: Add and display error
            return {
                ...state,
                serverEditing: false,
                serverErrorMessageVisible: true
            };
        case serverActionCreators.SERVER_CLEAR_ERROR:
            return {
                ...state,
                serverErrorMessageVisible: false,
            };
        case serverWithServicesActionCreators.SERVERWITHSERVICES_ALLOCATION_UPDATE: {
            const serverWithServicesIndexOfServerToUpdateServerWithServicess = state.server.map((server) => server.id).indexOf(action.serviceInstance.serverId);
            const serverWithServicesIndexToUpdateServerWithServicesa = state.server[serverWithServicesIndexOfServerToUpdateServerWithServicess].serviceAllocations.map((serviceAllocation) => serviceAllocation).indexOf(action.serviceInstance);
            return { ...state,
                server:
                    state.server.slice(0, serverWithServicesIndexOfServerToUpdateServerWithServicess)
                        .concat([{
                            ...state.server[serverWithServicesIndexOfServerToUpdateServerWithServicess],
                            serviceAllocations: state.server[serverWithServicesIndexOfServerToUpdateServerWithServicess].serviceAllocations.slice(0, serverWithServicesIndexToUpdateServerWithServicesa)
                                .concat([{
                                    ...state.server[serverWithServicesIndexOfServerToUpdateServerWithServicess].serviceAllocations[serverWithServicesIndexToUpdateServerWithServicesa],
                                    allocatedAssets: { value: action.newAllocation, valid: true }
                                    // TODO: above instead of valid true that could check valid again
                                }])
                                .concat(state.server[serverWithServicesIndexOfServerToUpdateServerWithServicess].serviceAllocations.slice(serverWithServicesIndexToUpdateServerWithServicesa + 1))
                        }])
                        .concat(state.server.slice(serverWithServicesIndexOfServerToUpdateServerWithServicess + 1))
            };
        }
        case serverWithServicesActionCreators.SERVICE_DELETE_SUCCESS: {
            const serviceDeleteSuccessServerIndex = state.server.map((server) => server.id).indexOf(action.serviceInstance.serverId);
            return { ...state,
                server:
                    state.server.slice(0, serviceDeleteSuccessServerIndex)
                        .concat([{
                            ...state.server[serviceDeleteSuccessServerIndex],
                            serviceAllocations: state.server[serviceDeleteSuccessServerIndex].serviceAllocations.filter((service: any) => service.id !== action.serviceInstance.id)
                        }])
                        .concat(state.server.slice(serviceDeleteSuccessServerIndex + 1))
            };
        }
        case serverWithServicesActionCreators.SERVICE_DELETE_ERROR:
            // TODO: Add and display error
            return {
                ...state,
                text: action.text,
                serverLoading: false
            };
        case serverWithServicesActionCreators.SERVERWITHSERVICES_UPDATE_ERROR:
            // TODO: Add and display error
            return {
                ...state,
                serverEditing: false
            };

        case serverWithServicesActionCreators.SERVERWITHSERVICES_UPDATE_SUCCESS:
            return { ...state,
                server:
                    state.server.map((server) => {
                        const addServiceInstance = server.id === action.newServiceInstance.serverId && action.isPost ? action.newServiceInstance : null;
                        let newServiceAllocations = server.serviceAllocations.map((serviceAllocation) => {
                            if (action.newServiceInstance.id === serviceAllocation.id) {
                                return {
                                    ...action.newServiceInstance,
                                    isBeingEdited: false
                                };
                            } else {
                                return serviceAllocation;
                            }
                        });
                        if (addServiceInstance) {
                            newServiceAllocations = newServiceAllocations.concat([addServiceInstance]);
                        }
                        return {
                            ...server,
                            serviceAllocations: newServiceAllocations
                        };
                    })
            };
        case licenseAllowanceActionCreators.ALLOCATION_ADD_SUCCESS:
            return {
                ...state,
                availableServices: state.availableServices && state.availableServices.length > 0 ? state.availableServices.concat([action.allocation.licensableService]) : [action.allocation.licensableService]
            };
        case licenseAllowanceActionCreators.ALLOCATION_DELETE_SUCCESS:
            return {
                ...state,
                availableServices: state.availableServices && state.availableServices.length > 1 ? state.availableServices.filter((availableService: LicenseModels.IServiceOption) => availableService.id !== action.allocation.licensableService.id) : []
            };
        default:
            return state;
    }
}

export function licenseAllowanceReducer(state = initialLicenseAllocationState, action: any): any {
    switch (action.type) {
        case licenseAllowanceActionCreators.ALLOCATION_GET_REQUEST:
            return {
                ...state,
                licenseAllowancesLoading: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_GET_SUCCESS:
            return {
                ...state,
                site: action.siteInfo.site,
                licenseAllowances: action.siteInfo.licenseAllowances,
                allocationIsAddingNew: false,
                licenseAllowancesLoading: false
            };
        case licenseAllowanceActionCreators.ALLOCATION_GET_ERROR:
            return {
                ...state,
                text: action.text,
                licenseAllowancesLoading: false,
                licenseAllowanceErrorMessageVisible: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_EDITABLE_ROW_ADD:
            return {
                ...state,
                licenseAllowances: state.licenseAllowances.concat([action.newAllocation]),
                allocationIsAddingNew: true,
                allocationIsEditing: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_EDIT_ROW: {
            action.allocationToEdit.isBeingEdited = true;
            const serviceToEditIndex = state.licenseAllowances.indexOf(action.allocationToEdit);
            const allocationToUpdate = serviceToEditIndex > -1 ? { ...state,
                licenseAllowances:
                    state.licenseAllowances.slice(0, serviceToEditIndex)
                        .concat([{
                            ...state.licenseAllowances[serviceToEditIndex],
                            isBeingEdited: true
                        }])
                        .concat(state.licenseAllowances.slice(serviceToEditIndex + 1))
            } : state;
            return { ...allocationToUpdate,
                allocationIsEditing: true,
                licenseAllowances: allocationToUpdate.licenseAllowances
                    .filter((licenseAllowances: any) =>
                        licenseAllowances.id !== -1)
            };
        }
        case licenseAllowanceActionCreators.ALLOCATION_RESET_CURRENTLY_EDITING: {
            const indexUpdateEditing = state.licenseAllowances.map((allowance) => allowance.isBeingEdited).indexOf(true);
            const currentAllocation = indexUpdateEditing > -1 ? { ...state,
                licenseAllowances:
                    state.licenseAllowances.slice(0, indexUpdateEditing)
                        .concat([{
                            ...state.licenseAllowances[indexUpdateEditing],
                            isBeingEdited: false
                        }])
                        .concat(state.licenseAllowances.slice(indexUpdateEditing + 1))
            } : state;
            return { ...currentAllocation,
                allocationIsAddingNew: false,
                licenseAllowances: currentAllocation.licenseAllowances
                    .filter((licenseAllowances: any) =>
                        licenseAllowances.id !== -1)
            };
        }
        case licenseAllowanceActionCreators.ALLOCATION_CANCEL_EDITING: {
            const indexUpdateCancelEditing = state.licenseAllowances.map((allowance) => allowance.isBeingEdited).indexOf(true);
            const currentAllocationForCancel = indexUpdateCancelEditing > -1 ? { ...state,
                licenseAllowances:
                    state.licenseAllowances.slice(0, indexUpdateCancelEditing)
                        .concat([{
                            ...state.licenseAllowances[indexUpdateCancelEditing],
                            isBeingEdited: false
                        }])
                        .concat(state.licenseAllowances.slice(indexUpdateCancelEditing + 1))
            } : state;
            return { ...currentAllocationForCancel,
                allocationIsEditing: false,
                licenseAllowances: currentAllocationForCancel.licenseAllowances
                    .filter((licenseAllowances: any) =>
                        licenseAllowances.id !== -1)
            };
        }
        case licenseAllowanceActionCreators.ALLOCATION_ADD_REQUEST:
            return { ...state,
                licenseAllowancesLoading: true,
            };
        case licenseAllowanceActionCreators.ALLOCATION_ADD_SUCCESS:
            return { ...state,
                licenseAllowances: state.licenseAllowances.concat([action.allocation]),
                licenseAllowancesLoading: false,
                allocationIsEditing: false,
                allocationIsAddingNew: false,
            };
        case licenseAllowanceActionCreators.ALLOCATION_ADD_ERROR:
            return {
                ...state,
                text: action.text,
                allocationIsEditing: false,
                licenseAllowancesLoading: false,
                licenseAllowanceErrorMessageVisible: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_CANCEL_REQUEST:
            return { ...state,

                allocationIsAddingNew: false,
                allocationIsEditing: false,
            };
        case licenseAllowanceActionCreators.ALLOCATION_DELETE_REQUEST: {
            const allocationToDeleteIndex = state.licenseAllowances.indexOf(action.allocationToDelete);
            const allocationToDelete = allocationToDeleteIndex > -1 ? { ...state,
                licenseAllowances:
                    state.licenseAllowances.slice(0, allocationToDeleteIndex)
                        .concat([{
                            ...state.licenseAllowances[allocationToDeleteIndex],
                            isBeingEdited: false
                        }])
                        .concat(state.licenseAllowances.slice(allocationToDeleteIndex + 1))
            } : state;
            return { ...allocationToDelete,
                licenseAllowances: allocationToDelete.licenseAllowances,
                licenseAllowancesLoading: false
            };
        }
        case licenseAllowanceActionCreators.ALLOCATION_DELETE_SUCCESS:
            return { ...state,
                licenseAllowances: state.licenseAllowances
                    .filter((licenseAllowances: any) =>
                        licenseAllowances.id !== action.allocation.id),
                licenseAllowancesLoading: false,
                allocationIsAddingNew: false,
            };
        case licenseAllowanceActionCreators.ALLOCATION_DELETE_ERROR:
            return {
                ...state,
                text: action.text,
                licenseAllowancesLoading: false,
                allocationIsAddingNew: false,
                licenseAllowanceErrorMessageVisible: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_UPDATE_REQUEST: {
            const allocationToRequestUpdateLookup = state.licenseAllowances.find(x => x.id === action.allocationToUpdate.id);
            const allocationToRequestUpdateIndex = allocationToRequestUpdateLookup === undefined ? -1 : state.licenseAllowances.indexOf(allocationToRequestUpdateLookup);
            const allocationToRequestUpdate = allocationToRequestUpdateIndex > -1 ? { ...state,
                licenseAllowances:
                    state.licenseAllowances.slice(0, allocationToRequestUpdateIndex)
                        .concat([{
                            ...state.licenseAllowances[allocationToRequestUpdateIndex],
                            isBeingEdited: false
                        }])
                        .concat(state.licenseAllowances.slice(allocationToRequestUpdateIndex + 1))
            } : state;
            return { ...allocationToRequestUpdate,
                allocationIsEditing: false,
                licenseAllowances: allocationToRequestUpdate.licenseAllowances
                    .filter((licenseAllowances: any) =>
                        licenseAllowances.id !== -1),
                serviceIsAddingNew: false,
            };
        }
        case licenseAllowanceActionCreators.ALLOCATION_UPDATE_SUCCESS: {
            const allocationToUpdateLookup = state.licenseAllowances.find(x => x.id === action.allocationToUpdate.id);
            const allocationToUpdateIndex = allocationToUpdateLookup === undefined ? -1 : state.licenseAllowances.indexOf(allocationToUpdateLookup);
            const allocationTotUpdate = allocationToUpdateIndex > -1 ? { ...state,
                licenseAllowances:
                    state.licenseAllowances.slice(0, allocationToUpdateIndex)
                        .concat([{
                            ...action.allocationToUpdate,
                            isBeingEdited: false,
                        }])
                        .concat(state.licenseAllowances.slice(allocationToUpdateIndex + 1))
            } : state;
            return { ...allocationTotUpdate,
                licenseAllowances: allocationTotUpdate.licenseAllowances
                    .filter((licenseAllowances: any) =>
                        licenseAllowances.id !== -1),
                allocationIsAddingNew: false,
                licenseAllowancesLoading: false
            };
        }
        case licenseAllowanceActionCreators.ALLOCATION_UPDATE_ERROR:
            return {
                ...state,
                text: action.text,
                licenseAllowancesLoading: false,
                licenseAllowanceErrorMessageVisible: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_CLEAR_ERROR:
            return {
                ...state,
                licenseAllowanceErrorMessageVisible: false,
            };
        default:
            return state;
    }
}

export function serviceOptionsReducer(state = initialServiceOptionsState, action: any): any {
    switch (action.type) {
        case licenseAllowanceActionCreators.ALLOCATION_ADD_SUCCESS:
            return {
                ...state,
                serviceOptions: {
                    ...state.serviceOptions,
                    licensableServices: state.serviceOptions.licensableServices.filter((service: any) => service.id !== action.allocation.licensableService.id)
                }
            };
        case licenseAllowanceActionCreators.ALLOCATION_DELETE_SUCCESS:
            return {
                ...state,
                serviceOptions: {
                    ...state.serviceOptions,
                    licensableServices: state.serviceOptions.licensableServices.concat([action.allocation.licensableService])
                }
            };
        case licenseAllowanceActionCreators.ALLOCATION_UPDATE_SUCCESS:
            return {
                ...state,
                serviceOptions: {
                    ...state.serviceOptions,
                    licensableServices: state.serviceOptions.licensableServices.filter((service: any) => service.id !== action.allocationToUpdate.licensableService.id)
                }
            };
        case licenseAllowanceActionCreators.ALLOCATION_EDIT_ROW:
            if (state.serviceOptions.licensableServices.indexOf(action.allocationToEdit.licensableService) < 0) {
                return {
                    ...state,
                    serviceOptions: {
                        ...state.serviceOptions,
                        licensableServices: state.serviceOptions.licensableServices.concat([action.allocationToEdit.licensableService])
                    }
                };
            }
            return state;
        case licenseAllowanceActionCreators.ALLOCATION_NEW_REQUEST:
            return {
                ...state,
                serviceOptionsLoading: true
            };
        case licenseAllowanceActionCreators.ALLOCATION_NEW_SUCCESS:
            return {
                ...state,
                serviceOptions: action.serviceOptions,
                serviceOptionsLoading: false
            };
        case licenseAllowanceActionCreators.ALLOCATION_NEW_ERROR:
            return {
                ...state,
                text: action.text,
                serviceOptionsLoading: false
            };
        default:
            return state;
    }
}

export function siteLicenseReducer(state = initialSiteLicenseState, action: any): any {
    switch (action.type) {
        case siteLicenseActionCreators.LICENSE_FILE_GET_REQUEST:
            return {
                ...state,
                licenseFileLoading: true
            };
        case siteLicenseActionCreators.GET_LICENSE_FILE_SUCCESS:
            return {
                ...state,
                licenseFileLoading: false
            };
        case siteLicenseActionCreators.GET_LICENSE_FILE_ERROR:
            return {
                ...state,
                text: action.text,
                licenseFileLoading: false
            };
        default:
            return state;
    }
}
